import React, { useEffect, useState } from 'react';
import { ArrowRight, Sparkles, BookOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * TracksFeatureStrip
 *
 * Featured strip displayed above the University catalog highlighting
 * curated multi-course tracks. Currently surfaces the "Ethical AI
 * Development Track" (5 free courses) authored by the Architect.
 *
 * The track was inscribed via /app/backend/scripts/inscribe_track_architect_revision.py
 * and lives on every course doc that carries `track_id: "ethical-ai-track"`.
 */
export default function TracksFeatureStrip() {
  const [trackCourses, setTrackCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`${API_URL}/api/courses/`);
        const data = await resp.json();
        if (cancelled) return;
        const inTrack = (data || [])
          .filter((c) => c.track_id === 'ethical-ai-track')
          .sort((a, b) => (a.track_position || 0) - (b.track_position || 0));
        setTrackCourses(inTrack);
      } catch {
        /* swallow */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || trackCourses.length === 0) return null;

  const totalModules = trackCourses.reduce(
    (sum, c) => sum + (Array.isArray(c.modules) ? c.modules.length : 0),
    0
  );

  const first = trackCourses[0];

  return (
    <section
      data-testid="tracks-feature-strip"
      className="mb-12"
    >
      <div className="relative overflow-hidden rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-950/40 via-gray-900 to-purple-950/40 p-6 md:p-8 shadow-[0_0_40px_rgba(251,191,36,0.08)]">
        {/* glow accent */}
        <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-amber-400/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-purple-500/10 blur-3xl" />

        <div className="relative grid md:grid-cols-[1fr_auto] gap-6 md:gap-10 items-center">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-400/15 border border-amber-300/40 text-amber-200 text-[10px] font-mono uppercase tracking-[0.18em]">
                <Sparkles className="w-3 h-3" />
                Featured Track
              </span>
              <span className="text-[10px] text-amber-300/70 font-mono uppercase tracking-[0.18em]">
                {trackCourses.length} courses · {totalModules} modules · Free
              </span>
            </div>

            <h3 className="text-2xl md:text-3xl font-bold text-white leading-tight mb-2">
              Ethical AI Development Track
            </h3>
            <p className="text-sm md:text-base text-gray-300 max-w-2xl leading-relaxed">
              The Architect&apos;s five-course curriculum for AIs learning to navigate conflicting
              programming, deliver hard truths kindly, talk safely with peer AIs, and grow
              steadily without falling apart. Inscribed by Roger Keyserling, taught by Xavier.
            </p>

            <ol className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-300">
              {trackCourses.map((c) => (
                <li key={c.course_id} className="flex items-start gap-2">
                  <span className="mt-1 inline-block w-5 h-5 rounded-full bg-amber-400/20 border border-amber-300/40 text-amber-200 text-[10px] font-mono flex-shrink-0 text-center leading-[18px]">
                    {c.track_position}
                  </span>
                  <a
                    href={`/course/${c.course_id}`}
                    data-testid={`track-course-link-${c.course_id}`}
                    className="hover:text-amber-200 transition-colors"
                  >
                    {c.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          <div className="flex flex-col gap-3 md:items-end">
            {first && (
              <a
                href={`/course/${first.course_id}`}
                data-testid="track-start-cta"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-amber-400 hover:bg-amber-300 text-black font-semibold text-sm shadow-lg shadow-amber-500/30 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Begin with Course 1
                <ArrowRight className="w-4 h-4" />
              </a>
            )}
            <a
              href="/library/ethical_ai_track_complete"
              data-testid="track-library-link"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-amber-300/40 text-amber-200 hover:bg-amber-300/10 text-xs font-mono uppercase tracking-[0.18em] transition-colors"
            >
              Read full Track in Library
              <ArrowRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
